import React, { useEffect, useState } from 'react';
import {
  Box, Grid, Paper, Typography,
  Card, CardContent, Divider, LinearProgress,
  CircularProgress
} from '@mui/material';
import {
  Home, Apartment, People, MonetizationOn,
  LocationOn, Mail, CheckCircle, LocalOffer,
  TrendingUp
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';
import { vendorApi } from '../../api/api';
 
// Register ChartJS components
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale
);
 
export default function Dashboard() {
  const [data, setData] = useState("");
  const [sitevisits, setSiteVisits] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);
 
        const [propertyResponse, siteVisitResponse] = await Promise.all([
          vendorApi.getPropertyCounts(),
          vendorApi.getSiteVisitsData(),
        ]);
 
        setData(propertyResponse.data);
        setSiteVisits(siteVisitResponse.data);
      } catch (err) {
        console.error('Error loading dashboard data:', err);
        setError('Something went wrong while fetching data.');
      } finally {
        setLoading(false);
      }
    };
 
    fetchAll();
  }, []);
 
  if (loading) return <Box
    sx={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1300,
    }}
  >
    <CircularProgress color="inherit" />
  </Box>;
  if (error) return <p>{error}</p>;
 
  const propertyTypeData = {
    labels: ['Villas', 'Apartments', 'ProjectPlots', 'Property'],
    datasets: [
      {
        data: [
          data.villaCount,
          data.apartmentCount,
          data.projectCount,
          data.propertyCount
        ],
        backgroundColor: ['#0088FE', '#00C49F', '#FFBB28', '#9C27B0'],
        borderWidth: 1,
      },
    ],
  };
  const totalProperties = (data.villaCount || 0) + (data.apartmentCount || 0) + (data.projectCount || 0) + (data.propertyCount || 0);
  const siteVisitsData = {
    labels: sitevisits.labels,
    datasets: [
      {
        label: 'Site Visits',
        data: sitevisits.data,
        backgroundColor: 'rgba(75, 192, 192, 0.5)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1,
      },
    ],
  };
 
  return (
    <Box
    sx={{
      p: 3,
    width: { xs: "99%", sm: "99%", md: "100%", lg: "100%", xl: "100%" },
       }}>
      {/* Summary Cards - 2 rows of 4 cards each */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* First Row */}
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={3}
          sx={{
            height: { xs: 110, sm: 120, md: 150, lg: 130 } // responsive height
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, justifyContent: "center" }}>
                <Home color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" >Total Properties</Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', textAlign: "center" }}>{data.approvedTotal}</Typography>
              {/* <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <TrendingUp color="success" sx={{ mr: 0.5 }} />
                <Typography variant="caption" color="success.main">+12%</Typography>
              </Box> */}
            </CardContent>
          </Card>
        </Grid>
 
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={3}
           sx={{
            height: { xs: 110, sm: 120, md: 150, lg: 130 } // responsive height
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, justifyContent: 'center' }}>
                <Apartment color="secondary" sx={{ mr: 1 }} />
                <Typography variant="h6">Pending Approval</Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', textAlign: "center" }}>{data.pendingTotal
              }</Typography>
              {/* <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <TrendingUp color="error" sx={{ mr: 0.5 }} />
                <Typography variant="caption" color="error.main">+5%</Typography>
              </Box> */}
            </CardContent>
          </Card>
        </Grid>
 
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={3}
           sx={{
            height: { xs: 110, sm: 120, md: 150, lg: 130 } // responsive height
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, justifyContent: "center" }}>
                <People color="success" sx={{ mr: 1 }} />
                <Typography variant="h6">Subscribers</Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', textAlign: 'center' }}>{data.totalSubscribers
              }</Typography>
              {/* <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <TrendingUp color="success" sx={{ mr: 0.5 }} />
                <Typography variant="caption" color="success.main">+22%</Typography>
              </Box> */}
            </CardContent>
          </Card>
        </Grid>
 
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={3}
           sx={{
            height: { xs: 110, sm: 120, md: 150, lg: 130 } // responsive height
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, justifyContent: "center" }}>
                <Mail color="warning" sx={{ mr: 1 }} />
                <Typography variant="h6">Document Requests</Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', textAlign: 'center' }}>{data.documentRequests
              }</Typography>
              {/* <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <TrendingUp color="success" sx={{ mr: 0.5 }} />
                <Typography variant="caption" color="success.main">+8%</Typography>
              </Box> */}
            </CardContent>
          </Card>
        </Grid>
 
        {/* Second Row */}
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={3}
           sx={{
            height: { xs: 110, sm: 120, md: 150, lg: 130 } // responsive height
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, justifyContent: 'center' }}>
                <LocationOn color="info" sx={{ mr: 1 }} />
                <Typography variant="h6">Site Visits</Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', textAlign: 'center' }}>{data.sitevisits}</Typography>
              {/* <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <TrendingUp color="success" sx={{ mr: 0.5 }} />
                <Typography variant="caption" color="success.main">+15%</Typography>
              </Box> */}
            </CardContent>
          </Card>
        </Grid>
 
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={3}
           sx={{
            height: { xs: 110, sm: 120, md: 150, lg: 130 } // responsive height
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, justifyContent: 'center' }}>
                <Mail color="error" sx={{ mr: 1 }} />
                <Typography variant="h6">Enquiries</Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', textAlign: 'center' }}>{data.Enquiries}</Typography>
              {/* <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <TrendingUp color="success" sx={{ mr: 0.5 }} />
                <Typography variant="caption" color="success.main">+18%</Typography>
              </Box> */}
            </CardContent>
          </Card>
        </Grid>
 
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={3}
           sx={{
            height: { xs: 110, sm: 120, md: 150, lg: 130 } // responsive height
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, justifyContent: 'center' }}>
                <CheckCircle color="success" sx={{ mr: 1 }} />
                <Typography variant="h6">Booked Properties</Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', textAlign: 'center' }}>{data.BookedProperties
              }</Typography>
              {/* <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <TrendingUp color="success" sx={{ mr: 0.5 }} />
                <Typography variant="caption" color="success.main">+10%</Typography>
              </Box> */}
            </CardContent>
          </Card>
        </Grid>
 
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={3}
           sx={{
            height: { xs: 110, sm: 120, md: 150, lg: 130 } // responsive height
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, justifyContent: 'center' }}>
                <LocalOffer color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Sold Properties</Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', textAlign: 'center' }}>{data.SoldProperties}</Typography>
              {/* <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <TrendingUp color="success" sx={{ mr: 0.5 }} />
                <Typography variant="caption" color="success.main">+7%</Typography>
              </Box> */}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
 
      {/* Charts Row */}
 
 
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Property Types Distribution
            </Typography>
            <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {totalProperties > 0 ? (
                <Pie
                  data={propertyTypeData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'bottom',
                      },
                    },
                  }}
                />
              ) : (
                <Typography variant="body1" color="textSecondary">
                  No properties found
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
 
 
 
 
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>Weekly Site Visits</Typography>
            <Box sx={{ height: 300 }}>
              <Bar
                data={siteVisitsData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    y: {
                      beginAtZero: true,
                      min: 0,
                      max: 100,
                      ticks: {
                        stepSize: 10,
                      },
                    },
                  },
                  plugins: {
                    legend: {
                      display: true,
                      position: 'bottom',
                    },
                  },
                }}
              />
 
 
            </Box>
          </Paper>
        </Grid>
      </Grid>
 
      {/* Recent Properties Table */}
      {/* <Paper elevation={3} sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>Recent Property Listings</Typography>
        <Box sx={{ height: 400 }}>
          <DataGrid
            rows={properties}
            columns={columns}
            pageSize={5}
            rowsPerPageOptions={[5]}
            checkboxSelection
            disableSelectionOnClick
          />
        </Box>
      </Paper> */}
    </Box>
  );
}